import { Button, Group, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { i18nKeys, type TranslateFunction, useTranslation } from 'locales';
import { type Invitation, useSendInvitation } from 'shared/hooks/use-send-invitation';
import { emailRegex } from 'shared/utils/validation';

type InviteCollaboratorFormProps = {
  onClose: () => void;
};

export default function InviteCollaboratorForm({ onClose }: InviteCollaboratorFormProps) {
  const { t } = useTranslation();
  const { sendInvitation, isSendInvitationLoading } = useSendInvitation();

  const form = useForm<Invitation>({
    validateInputOnBlur: true,
    initialValues: { firstName: '', lastName: '', email: '' },
    validate: {
      firstName: validateRequired(t),
      lastName: validateRequired(t),
      email: validateEmail(t),
    },
  });

  const handleSubmit = form.onSubmit(invitation =>
    sendInvitation(invitation, { onSuccess: onClose }),
  );

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput
          label={t(i18nKeys.FORM.FIRSTNAME)}
          withAsterisk
          data-autofocus
          {...form.getInputProps('firstName')}
        />
        <TextInput
          label={t(i18nKeys.FORM.LASTNAME)}
          withAsterisk
          {...form.getInputProps('lastName')}
        />
        <TextInput
          label={t(i18nKeys.EMAIL)}
          withAsterisk
          type="email"
          {...form.getInputProps('email')}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {t(i18nKeys.CANCEL)}
          </Button>
          <Button type="submit" disabled={!form.isValid()} loading={isSendInvitationLoading}>
            {t(i18nKeys.SAVE)}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

const validateRequired = (t: TranslateFunction) => (value: string) =>
  value.trim().length === 0 ? t(i18nKeys.ERROR.FIELD_REQUIRED) : null;

const validateEmail = (t: TranslateFunction) => (value: string) => {
  if (value.trim().length === 0) return t(i18nKeys.ERROR.FIELD_REQUIRED);
  return emailRegex.test(value) ? null : t(i18nKeys.ERROR.INVALID_EMAIL);
};
